import { Icon } from "@/components/Icon/Icon";
import {
  heatColor,
  heatColorDelta,
  heatColorGray,
} from "./profileVector";
import type { ChangeKind } from "./ChangeSymbol";
import type { StatusEntry } from "./StatusFeed";

export type AIBannerPhase = "formulating" | "review" | "failed";

export type AIChange = {
  kind: ChangeKind;
  compoundId: string;
  prevPpm?: number;
  newPpm?: number;
};

type AIBannerProps = {
  phase: AIBannerPhase;
  statusLog: StatusEntry[];
  changes: AIChange[];
  onAccept: () => void;
  onReject: () => void;
  /** Live Loss from useWorkbenchViz (running banner only). */
  loss: number | null;
  /** Target profile vector — real values from useWorkbenchViz. */
  targetProfile: number[] | null;
  /** Current m(S) profile vector — real values from useWorkbenchViz. */
  currentProfile: number[] | null;
  /** When phase='failed', the error message to surface in the banner. */
  errorMessage?: string | null;
};

export function AIBanner({
  phase,
  statusLog,
  changes,
  onAccept,
  onReject,
  loss,
  targetProfile,
  currentProfile,
  errorMessage,
}: AIBannerProps) {
  if (phase === "formulating") {
    return (
      <RunBanner
        loss={loss}
        targetProfile={targetProfile}
        currentProfile={currentProfile}
        statusLog={statusLog}
      />
    );
  }
  if (phase === "failed") {
    return (
      <FailedBanner
        errorMessage={errorMessage ?? "unknown error"}
        onReject={onReject}
      />
    );
  }
  return <ReviewBanner changes={changes} onAccept={onAccept} onReject={onReject} />;
}

// ── Run banner ────────────────────────────────────────────────────
// Dark-green gradient card with the live Loss tile and a three-row profile
// diff (Target/Δ/Current). The Δ row visualizes the per-cell squared
// difference the AI is trying to minimize. All three profile rows render
// real values from useWorkbenchViz; the grid template adapts to the
// pin's embedding_dim (typically 111, but variable per pin).
function RunBanner({
  loss,
  targetProfile,
  currentProfile,
  statusLog,
}: {
  loss: number | null;
  targetProfile: number[] | null;
  currentProfile: number[] | null;
  statusLog: StatusEntry[];
}) {
  const lastStatus = statusLog[statusLog.length - 1];
  // Pick the longer of the two profiles to drive the column count — both
  // should match in practice (same pin), but guard against missing data.
  const dim = Math.max(
    targetProfile?.length ?? 0,
    currentProfile?.length ?? 0,
  );
  const target = targetProfile ?? new Array<number>(dim).fill(0);
  const current = currentProfile ?? new Array<number>(dim).fill(0);
  const gridStyle = dim > 0
    ? { gridTemplateColumns: `repeat(${dim}, 1fr)` }
    : undefined;

  return (
    <div className="af-banner af-banner-run">
      <div className="af-banner-row af-banner-row-head">
        <span className="af-orb">
          <Icon name="sparkle" size={12} />
        </span>
        <span className="af-banner-title">Auto-Formulating</span>
        <span style={{ flex: 1 }} />
        <div className="af-loss-tile">
          <span className="af-loss-label">Loss</span>
          <span className="af-loss-value">
            {loss == null ? "—" : loss.toFixed(2)}
          </span>
        </div>
      </div>

      <div className="af-profilediff">
        <div className="af-profilediff-row">
          <span className="af-profilediff-label">
            <span
              className="af-profilediff-dot"
              style={{ background: "rgba(236,235,225,0.7)" }}
            />
            Target
          </span>
          <div className="af-profilediff-vec af-profilediff-vec-gray" style={gridStyle}>
            {target.map((v, i) => (
              <div key={i} style={{ background: heatColorGray(v) }} />
            ))}
          </div>
          <span className="af-profilediff-note">constant</span>
        </div>

        <div className="af-profilediff-row af-profilediff-row-delta">
          <span className="af-profilediff-label af-profilediff-label-delta">
            <span className="af-delta-op">−</span>
            <span>Δ</span>
          </span>
          <div className="af-profilediff-vec-delta" style={gridStyle}>
            {target.map((tv, i) => {
              const diff = Math.abs(tv - (current[i] ?? 0));
              return <div key={i} style={{ background: heatColorDelta(diff) }} />;
            })}
            <div className="af-delta-sweep" />
          </div>
          <span className="af-profilediff-note af-profilediff-note-delta">⇒ Loss</span>
        </div>

        <div className="af-profilediff-row">
          <span className="af-profilediff-label">
            <span
              className="af-profilediff-dot"
              style={{
                background: "var(--brand-lime)",
                boxShadow: "0 0 6px rgba(110,192,74,0.5)",
              }}
            />
            Current
          </span>
          <div className="af-profilediff-vec af-profilediff-vec-live" style={gridStyle}>
            {current.map((v, i) => (
              <div key={i} style={{ background: heatColor(v) }} />
            ))}
          </div>
          <span className="af-profilediff-note">tracking</span>
        </div>
      </div>

      <div className="af-banner-row af-banner-row-status">
        <span className="mono af-banner-status" key={lastStatus?.id ?? "_"}>
          <span style={{ color: "var(--brand-lime)", marginRight: 6 }}>•</span>
          {lastStatus?.text ?? "Embedding context…"}
        </span>
      </div>
    </div>
  );
}

// ── Failed banner ──────────────────────────────────────────────────
// Surfaces an AF error (POST failure, SSE failed event, or connection
// drop). User dismisses via "Reject all" to return to idle.
function FailedBanner({
  errorMessage,
  onReject,
}: {
  errorMessage: string;
  onReject: () => void;
}) {
  return (
    <div className="af-banner af-banner-review">
      <div className="af-banner-row af-banner-row-head">
        <span className="af-orb af-orb-done" style={{ background: "#872018", borderColor: "#872018" }}>
          <Icon name="alert" size={12} />
        </span>
        <span className="af-banner-title" style={{ whiteSpace: "nowrap" }}>
          Auto-Formulate failed
        </span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className="btn xs af-banner-cta"
          onClick={onReject}
        >
          <Icon name="reset" size={11} />
          Dismiss
        </button>
      </div>
      <div className="af-banner-row af-banner-row-status">
        <span className="mono af-banner-status">{errorMessage}</span>
      </div>
    </div>
  );
}

// ── Review banner ─────────────────────────────────────────────────
function ReviewBanner({
  changes,
  onAccept,
  onReject,
}: {
  changes: AIChange[];
  onAccept: () => void;
  onReject: () => void;
}) {
  const adds = changes.filter((c) => c.kind === "add").length;
  const edits = changes.filter((c) => c.kind === "edit").length;
  const removes = changes.filter((c) => c.kind === "remove").length;
  return (
    <div className="af-banner af-banner-review">
      <div className="af-banner-row af-banner-row-head">
        <span className="af-orb af-orb-done">
          <Icon name="check" size={12} />
        </span>
        <span className="af-banner-title" style={{ whiteSpace: "nowrap" }}>
          Review proposed changes
        </span>
        <span className="af-pillgroup">
          <span className="af-pill af-pill-add">+{adds}</span>
          <span className="af-pill af-pill-edit">Δ{edits}</span>
          <span className="af-pill af-pill-remove">−{removes}</span>
        </span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className="btn xs af-banner-cta"
          onClick={onReject}
          style={{ borderColor: "var(--line-strong)" }}
        >
          <Icon name="reset" size={11} />
          Reject all
        </button>
        <button
          type="button"
          className="btn xs primary af-banner-cta"
          onClick={onAccept}
        >
          <Icon name="check" size={11} />
          Accept formulation
        </button>
      </div>
      <div className="af-banner-row af-banner-row-status">
        <span className="mono af-banner-status">
          Review the proposed formulation, fine-tune any row, then click{" "}
          <strong>Accept formulation</strong> to apply.
        </span>
      </div>
    </div>
  );
}
